import { ChatWorkspace } from '@pwa/components/chat/chat-workspace';
import { NoWorkspacesOnboarding } from '@pwa/components/chat/no-workspaces-onboarding';
import { useSelectedOrganizationId } from '@pwa/lib/org-context';
import { workspaceQueries } from '@pwa/queries/workspaces/workspaces';
import { Skeleton } from '@repo/ui/components/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { generateId } from 'better-auth';
import { useCallback, useMemo } from 'react';

export const Route = createFileRoute('/_dashboard/org/$orgId/')({
	component: RouteComponent,
});

/**
 * Mirrors the ChatWorkspace layout (scope label, message column, prompt
 * input) with `animate-pulse` placeholders so the dashboard doesn't pop
 * in blank while the workspaces query is resolving.
 */
function ChatWorkspaceSkeleton() {
	return (
		<div aria-busy='true' aria-live='polite' className='flex flex-col flex-1 min-h-0 gap-2 px-1 md:py-2' role='status'>
			<div className='container mx-auto flex max-w-3xl items-center gap-1.5 self-center'>
				<Skeleton className='h-3 w-24' />
			</div>
			<div className='flex-1 container mx-auto max-w-3xl flex flex-col gap-4 py-8'>
				<div className='flex gap-2 items-start'>
					<Skeleton className='size-8 rounded-full shrink-0' />
					<div className='flex-1 flex flex-col gap-2'>
						<Skeleton className='h-5 w-40' />
						<Skeleton className='h-4 w-full' />
						<Skeleton className='h-4 w-5/6' />
					</div>
				</div>
				<div className='flex gap-2 justify-end'>
					<Skeleton className='h-10 w-1/2 rounded-xl' />
				</div>
				<div className='flex gap-2 items-start'>
					<Skeleton className='size-8 rounded-full shrink-0' />
					<div className='flex-1 flex flex-col gap-2'>
						<Skeleton className='h-4 w-full' />
						<Skeleton className='h-4 w-11/12' />
						<Skeleton className='h-4 w-2/3' />
					</div>
				</div>
			</div>
			<div className='container mx-auto max-w-3xl'>
				<Skeleton className='h-24 w-full rounded-xl' />
			</div>
		</div>
	);
}

function RouteComponent() {
	const organizationId = useSelectedOrganizationId();

	// invariant: this route has $orgId in the path
	if (!organizationId) throw new Error('Route requires organizationId');

	const { data, isLoading } = useQuery(workspaceQueries.list(organizationId));
	const navigate = useNavigate();
	const id = useMemo(() => generateId(8), []);

	const workspaces = data?.workspaces ?? [];

	// After a workspace is created from the onboarding form, drop the user into
	// the new workspace workspace via the /ws/:wsId route.
	const handleWorkspaceCreated = useCallback(
		(workspaceId: string) => {
			if (!organizationId) return;
			void navigate({ to: '/org/$orgId/ws/$wsId', params: { orgId: organizationId, wsId: workspaceId } });
		},
		[navigate, organizationId],
	);

	if (isLoading) {
		return <ChatWorkspaceSkeleton />;
	}

	// No workspaces at all → onboarding takes precedence over any scope.
	if (workspaces.length === 0) {
		return <NoWorkspacesOnboarding onWorkspaceCreated={handleWorkspaceCreated} />;
	}

	return <ChatWorkspace chatId={id} initialMessages={[]} session={{ kind: 'draft' }} />;
}
